// src/ConsumerApp/Screens/Account/views/OffersView.js

import React, { useState, useMemo } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import Percent from '../../../../assets/icons/Percent.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';

// Shared Hooks
import { useGetDiscountsByCustomer } from '../../../api/offers/discounts.hooks';

const TABS = ['Available', 'Used', 'Expired'];

export default function OffersView() {
  // 1. Session Context (Direct Browser Storage)
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. Active Tab State
  const [selectedTab, setSelectedTab] = useState('Available');
  const [copiedCode, setCopiedCode] = useState(null);

  // 3. API Query
  const {
    data: discountsResponse,
    isLoading: isLoadingDiscounts,
    refetch: refetchDiscounts,
    isRefetching,
  } = useGetDiscountsByCustomer(customerId);

  const allDiscounts = useMemo(() => {
    if (Array.isArray(discountsResponse)) return discountsResponse;
    if (Array.isArray(discountsResponse?.data)) return discountsResponse.data;
    return [];
  }, [discountsResponse]);

  // 4. Tab Partitioning
  const availableDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.status === 'created' || d.status === 'partially_used'),
    [allDiscounts]
  );

  const usedDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.status === 'used'),
    [allDiscounts]
  );

  const expiredDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.status === 'expired'),
    [allDiscounts]
  );

  const currentList = useMemo(() => {
    if (selectedTab === 'Available') return availableDiscounts;
    if (selectedTab === 'Used') return usedDiscounts;
    return expiredDiscounts;
  }, [selectedTab, availableDiscounts, usedDiscounts, expiredDiscounts]);

  // 5. Copy to Clipboard
  const handleCopyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>Offers & Coupons</h2>
          <span style={styles.headerSubtitle}>
            View available promotions, volume discounts, and voucher benefits
          </span>
        </div>

        <button
          type="button"
          style={styles.refreshBtn}
          onClick={() => refetchDiscounts()}
          disabled={isRefetching}
          title="Refresh offers"
        >
          <img
            src={ClockCounterClockwise}
            alt="refresh"
            style={{
              width: 14,
              height: 14,
              opacity: 0.7,
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>{isRefetching ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      {/* ── Segmented Tabs ── */}
      <div style={styles.segmentedContainer}>
        {TABS.map((tab) => {
          const isActive = selectedTab === tab;
          const count =
            tab === 'Available'
              ? availableDiscounts.length
              : tab === 'Used'
              ? usedDiscounts.length
              : expiredDiscounts.length;

          return (
            <button
              key={tab}
              type="button"
              style={{
                ...styles.tabBtn,
                ...(isActive ? styles.activeTabBtn : {}),
              }}
              onClick={() => setSelectedTab(tab)}
            >
              <span>{tab}</span>
              <span
                style={{
                  ...styles.tabCountBadge,
                  backgroundColor: isActive ? '#FEE2E2' : '#E2E8F0',
                  color: isActive ? '#C7061F' : '#64748B',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Coupons Grid / List ── */}
      {isLoadingDiscounts ? (
        <div style={styles.loadingBox}>
          <span>Fetching active discount coupons...</span>
        </div>
      ) : currentList.length === 0 ? (
        <div style={styles.emptyBox}>
          <div style={styles.emptyIconCircle}>
            <img src={Percent} alt="percent" style={{ width: 24, height: 24, opacity: 0.4 }} />
          </div>
          <h4 style={styles.emptyTitle}>No {selectedTab.toLowerCase()} coupons</h4>
          <p style={styles.emptySubtitle}>
            {selectedTab === 'Available'
              ? 'Check back later for newly released commercial discount vouchers.'
              : `You do not have any ${selectedTab.toLowerCase()} coupons on file.`}
          </p>
        </div>
      ) : (
        <div style={styles.couponGrid}>
          {currentList.map((coupon, idx) => {
            const isAvailable = selectedTab === 'Available';
            const isUsed = selectedTab === 'Used';
            const isExpired = selectedTab === 'Expired';
            const isCopied = copiedCode === coupon.code;

            const validityText = coupon.valid_till
              ? `Valid till ${new Date(coupon.valid_till).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}`
              : 'No expiry date';

            const minOrderText = coupon.min_order_value
              ? `Min. order ₹${Number(coupon.min_order_value).toLocaleString('en-IN')}`
              : null;

            return (
              <div
                key={coupon.discount_id || coupon._id || idx}
                style={{
                  ...styles.couponCard,
                  opacity: isUsed || isExpired ? 0.65 : 1,
                }}
              >
                {/* Top Row: Coupon Code & Copy / Apply Button */}
                <div style={styles.couponTopRow}>
                  <div style={styles.codePill}>
                    <span style={styles.codeText}>{coupon.code || 'PROMO'}</span>
                  </div>

                  {isAvailable && (
                    <button
                      type="button"
                      style={styles.copyBtn}
                      onClick={() => handleCopyCode(coupon.code)}
                    >
                      {isCopied ? 'Copied!' : 'Copy Code'}
                    </button>
                  )}

                  {isUsed && <span style={styles.statusLabelUsed}>Used</span>}
                  {isExpired && <span style={styles.statusLabelExpired}>Expired</span>}
                </div>

                {/* Coupon Title & Description */}
                <div style={styles.couponContent}>
                  <h4 style={styles.couponTitle}>{coupon.title || 'Special Promotion'}</h4>
                  <p style={styles.couponDesc}>
                    {coupon.sub_text || 'Get a special discount on your upcoming construction order.'}
                  </p>
                </div>

                {/* Dashed Border Separator */}
                <div style={styles.dashedDivider} />

                {/* Bottom Metadata */}
                <div style={styles.couponFooter}>
                  <span style={styles.metaValidity}>{validityText}</span>
                  {minOrderText && <span style={styles.metaMinOrder}>{minOrderText}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    outline: 'none',
  },
  segmentedContainer: {
    display: 'inline-flex',
    backgroundColor: '#F5F3EF',
    borderRadius: 10,
    padding: 3,
    gap: 4,
    alignSelf: 'flex-start',
    border: '1px solid #E2E8F0',
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    backgroundColor: 'transparent',
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: '500',
    color: '#64748B',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
  },
  activeTabBtn: {
    backgroundColor: '#FFFFFF',
    color: '#2B2A29',
    fontWeight: '700',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  tabCountBadge: {
    fontSize: 11,
    fontWeight: '700',
    borderRadius: 999,
    padding: '1px 6px',
    minWidth: 16,
    textAlign: 'center',
  },
  couponGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  },
  couponCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    position: 'relative',
  },
  couponTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  codePill: {
    backgroundColor: '#FFF1F2',
    border: '1px dashed #F87171',
    borderRadius: 6,
    padding: '4px 10px',
  },
  codeText: {
    fontSize: 13,
    fontWeight: '800',
    color: '#C7061F',
    letterSpacing: '0.5px',
  },
  copyBtn: {
    background: 'none',
    border: 'none',
    color: '#C7061F',
    fontWeight: '700',
    fontSize: 12.5,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 6,
  },
  statusLabelUsed: {
    fontSize: 11.5,
    fontWeight: '700',
    color: '#64748B',
    backgroundColor: '#F1F5F9',
    padding: '2px 8px',
    borderRadius: 4,
  },
  statusLabelExpired: {
    fontSize: 11.5,
    fontWeight: '700',
    color: '#DC2626',
    backgroundColor: '#FEF2F2',
    padding: '2px 8px',
    borderRadius: 4,
  },
  couponContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  couponTitle: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  couponDesc: {
    margin: 0,
    fontSize: 12,
    color: '#64748B',
    lineHeight: '17px',
  },
  dashedDivider: {
    height: 1,
    borderTop: '1px dashed #E2E8F0',
    margin: '2px 0',
  },
  couponFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11.5,
  },
  metaValidity: {
    color: '#707B8C',
    fontWeight: '500',
  },
  metaMinOrder: {
    color: '#2B2A29',
    fontWeight: '600',
  },
  loadingBox: {
    padding: '48px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13.5,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '6px 0 0 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
};